import { useState, useEffect, useCallback } from "react";
import { AttachmentMeta } from "@/indexedDB/types";
import { isUserAuthenticated } from "../../auth";
import { getOrDecapsulateCSK } from "../../cryptoCache";
import { useAuthState } from "@/zustand/AuthState";
import { addMediaAttachmentToLocalDB } from "@/indexedDB/services/attachments";
import { refreshAccessToken } from "@/server/apiService";

interface UseVideoStreamOptions {
  enabled?: boolean;
  chatId?: string;
  messageId?: string;
  messageTimestamp?: string;
}

function useVideoStream(
  meta: AttachmentMeta,
  eCSK: string,
  options: UseVideoStreamOptions = {},
): {
  src: string | null;
  loading: boolean;
  error: Error | null;
  streamError: string | null;
} {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Reason string pushed by the SW when a stream fails mid-playback (the
  // <video> onError alone can't tell "upload failed" from "still uploading"
  // from "decrypt error"). Null until such an event arrives.
  const [streamError, setStreamError] = useState<string | null>(null);

  const { enabled = true, chatId, messageId, messageTimestamp } = options;

  const registerAndStream = useCallback(async () => {
    if (!enabled || !meta.isLargeAttachment || !meta.attachmentId) {
      setSrc(null);
      setLoading(false);
      setError(null);
      return;
    }

    const userFingerPrint = isUserAuthenticated();
    if (!userFingerPrint) {
      setError(new Error("Not authenticated"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setSrc(null);
    setError(null);
    setStreamError(null);

    try {
      const csk = await getOrDecapsulateCSK({
        username: userFingerPrint.username,
        dk: userFingerPrint.dk,
        ePrK: userFingerPrint.ePrK,
        eCSK,
      });

      const token = useAuthState.getState().tk;

      // navigator.serviceWorker.ready never rejects — if no SW is registered
      // (e.g. dev mode or first install) it hangs forever, leaving the loading
      // indicator stuck. Race it against a 5 s timeout so we always settle.
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Service worker not available")),
            5000,
          ),
        ),
      ]);

      // Prefer the controller (the SW managing this page's fetches) over
      // registration.active so REGISTER_CSK always reaches the same instance
      // that will intercept /sw-stream requests.
      const sw = navigator.serviceWorker.controller ?? registration.active;
      if (!sw) {
        throw new Error("No active service worker");
      }

      // Build uploadMeta only when the upload is still in progress so the SW
      // uses the pre-assembly endpoint and starts a retry pre-fetch loop.
      // chunkSize must match the plaintext chunk size used in messages.ts (5 MB).
      // Guard fileSize > 0: a zero fileSize produces totalChunks = 0, which
      // would make the prefetch loop exit immediately and break streaming.
      const UPLOAD_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
      const uploadMeta =
        meta.uploadState?.status === "uploading" && meta.fileSize > 0
          ? {
              totalChunks: Math.ceil(meta.fileSize / UPLOAD_CHUNK_SIZE_BYTES),
              plainTextSize: meta.fileSize,
              chunkSize: UPLOAD_CHUNK_SIZE_BYTES,
              contentType: meta.fileType,
            }
          : undefined;

      await new Promise<void>((resolve, reject) => {
        const channel = new MessageChannel();
        const timeout = setTimeout(
          () => reject(new Error("Service worker did not acknowledge")),
          3000,
        );
        channel.port1.onmessage = () => {
          clearTimeout(timeout);
          resolve();
        };
        sw.postMessage(
          {
            type: "REGISTER_CSK",
            uploadId: meta.attachmentId,
            csk,
            token,
            chatId,
            messageId,
            messageTimestamp,
            filename: meta.filename,
            uploadMeta,
          },
          [channel.port2],
        );
      });

      const streamUrl = `/sw-stream?uploadId=${encodeURIComponent(meta.attachmentId)}`;

      setSrc(streamUrl);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to initialize stream"),
      );
      setLoading(false);
    }
  }, [
    enabled,
    meta.isLargeAttachment,
    meta.attachmentId,
    meta.filename,
    meta.fileType,
    meta.fileSize,
    meta.uploadState,
    eCSK,
    chatId,
    messageId,
    messageTimestamp,
  ]);

  // Initial registration and whenever dependencies change
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) {
        return;
      }
      await registerAndStream();
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [registerAndStream]);

  // Re-register whenever a new service worker takes over the page
  useEffect(() => {
    if (!enabled || !meta.isLargeAttachment || !meta.attachmentId) {
      return;
    }

    // No-SW environments (insecure origin, private modes, policy-disabled):
    // registering listeners would throw synchronously and break the viewer —
    // registerAndStream already surfaces the graceful "Service worker not
    // available" error for these. Capture the container so cleanup doesn't
    // depend on the global still being present at unmount time.
    if (!("serviceWorker" in navigator)) {
      return;
    }
    const sw = navigator.serviceWorker;

    const handleControllerChange = () => {
      registerAndStream();
    };

    sw.addEventListener("controllerchange", handleControllerChange);
    return () => {
      sw.removeEventListener("controllerchange", handleControllerChange);
    };
  }, [enabled, meta.isLargeAttachment, meta.attachmentId, registerAndStream]);

  // Re-register when the SW evicts the CSK (token expiry → TOKEN_EXPIRED, or SW
  // restart with empty in-memory cache → CSK_EVICTED)
  useEffect(() => {
    if (!enabled || !meta.isLargeAttachment || !meta.attachmentId) {
      return;
    }

    if (!("serviceWorker" in navigator)) {
      return;
    }
    const sw = navigator.serviceWorker;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.uploadId !== meta.attachmentId) {
        return;
      }
      if (event.data?.type === "TOKEN_EXPIRED") {
        // The SW's bearer token expired. A plain re-register would reuse the
        // same expired token (a stream 401 never reaches apiService's refresh
        // interceptor) and 401 again, looping — this is the long-video death at
        // the token TTL. Refresh FIRST (shared queue → coalesces with any API
        // refresh, updates the store → the SW gets it via UPDATE_TOKEN), then
        // re-register with the fresh token. On failure (dead session) DON'T
        // re-register — that would just 401-loop; the SW's STREAM_ERROR already
        // surfaces "session expired", and logout is left to the next API call.
        refreshAccessToken().then(
          () => registerAndStream(),
          () => {},
        );
      } else if (event.data?.type === "CSK_EVICTED") {
        // SW restarted with an empty in-memory cache; the token is still valid,
        // so just re-register (no refresh needed).
        registerAndStream();
      }
    };

    sw.addEventListener("message", handleMessage);
    return () => {
      sw.removeEventListener("message", handleMessage);
    };
  }, [enabled, meta.isLargeAttachment, meta.attachmentId, registerAndStream]);

  // Capture stream-time failure reasons pushed out-of-band by the SW. The
  // <video> element's onError fires for any stream abort but carries no
  // detail, so the SW posts a STREAM_ERROR with a reason the UI can map to a
  // specific message (upload failed vs still uploading vs decrypt/auth error).
  useEffect(() => {
    if (!enabled || !meta.isLargeAttachment || !meta.attachmentId) {
      return;
    }

    if (!("serviceWorker" in navigator)) {
      return;
    }
    const sw = navigator.serviceWorker;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "STREAM_ERROR" &&
        event.data.uploadId === meta.attachmentId
      ) {
        setStreamError(
          typeof event.data.reason === "string"
            ? (event.data.reason as string)
            : "unknown",
        );
      }
    };

    sw.addEventListener("message", handleMessage);
    return () => {
      sw.removeEventListener("message", handleMessage);
    };
  }, [enabled, meta.isLargeAttachment, meta.attachmentId]);

  // Persist the fully-assembled file to IndexedDB when the SW signals completion
  useEffect(() => {
    if (!enabled || !meta.isLargeAttachment || !meta.attachmentId) {
      return;
    }
    if (!("serviceWorker" in navigator)) {
      return;
    }
    const sw = navigator.serviceWorker;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type !== "ATTACHMENT_COMPLETE" ||
        event.data.uploadId !== meta.attachmentId ||
        !(event.data.blob instanceof Blob)
      ) {
        return;
      }

      const {
        blob,
        chatId: swChatId,
        messageId: swMessageId,
        messageTimestamp: swTimestamp,
        filename,
        fileType,
      } = event.data as {
        blob: Blob;
        chatId?: string;
        messageId?: string;
        messageTimestamp?: string;
        filename: string;
        fileType: string;
      };

      // Await the write before ACKing: the SW deletes OPFS chunks on ACK,
      // so we must confirm the DB write succeeded first to avoid data loss.
      void addMediaAttachmentToLocalDB({
        id: meta.attachmentId,
        chatId: swChatId ?? chatId ?? "",
        messageId: swMessageId ?? messageId ?? "",
        // Never "": an empty timestamp sorted BEFORE every real one, making the
        // just-assembled (largest!) video the permanent first eviction victim.
        timestamp: swTimestamp ?? messageTimestamp ?? new Date().toISOString(),
        content: blob,
        fileType: fileType ?? meta.fileType,
        filename: filename || meta.filename,
        isLargeAttachment: true,
      })
        .then(() => {
          const controller = sw.controller;
          controller?.postMessage({
            type: "ATTACHMENT_COMPLETE_ACK",
            uploadId: meta.attachmentId,
          });
        })
        .catch((persistErr) => {
          // Deliberately do NOT ACK on failure: the SW frees its in-memory blob
          // and OPFS chunks only on ACK, so withholding it lets the SW's
          // timeout fallback (or a later re-register) reclaim them instead of
          // leaking. The assembled file still exists server-side, so reopening
          // the video simply re-streams it.
          // eslint-disable-next-line no-console
          console.warn(
            `[chunked-upload] failed to persist assembled attachment ${meta.attachmentId}; not ACKing`,
            persistErr,
          );
        });
    };

    sw.addEventListener("message", handleMessage);
    return () => {
      sw.removeEventListener("message", handleMessage);
    };
  }, [
    enabled,
    meta.isLargeAttachment,
    meta.attachmentId,
    meta.fileType,
    meta.filename,
    chatId,
    messageId,
    messageTimestamp,
  ]);

  return { src, loading, error, streamError };
}

export default useVideoStream;
